import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Ticket } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { type JudgePattern } from '../../services/api/judge';
import { createTicketFromPattern } from '../../services/api/tickets';
import { getApiErrorDetails } from '../../utils/api-error';
import { formatDateTime } from '../../utils/datetime';

import { rootCauseLabel } from './kvalitet-labels';
import { KvalitetSessionLink } from './KvalitetSessionLink';
import { ticketTabParams } from './sessions-tab';

// Finns redan ett utkast eller en issue för veckans mönster öppnas den i stället
// för att skapa en till. Byte till tickets-fliken läggs i historiken, så att
// tillbaka-knappen ger samma kvalitetsfilter igen.
const PatternTicketButton = ({ pattern }: { pattern: JudgePattern }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const mutation = useMutation({
    mutationFn: () =>
      createTicketFromPattern({
        rootCause: pattern.rootCause,
        isoYear: pattern.isoYear,
        isoWeek: pattern.isoWeek,
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets-count-draft'] });
      const ticketId = 'ticket' in result ? result.ticket.id : result.duplicateOf;
      setSearchParams(ticketTabParams(searchParams, ticketId));
    },
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-indigo-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-500 disabled:bg-slate-300"
      >
        {mutation.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Ticket className="h-3 w-3" />
        )}
        Skapa ticket
      </button>
      {mutation.error && (
        <p className="text-xs text-red-700">
          Kunde inte skapa ticket — {getApiErrorDetails(mutation.error)}
        </p>
      )}
    </div>
  );
};

interface KvalitetPatternsTableProps {
  patterns: JudgePattern[];
  onShowWeek: (pattern: JudgePattern) => void;
}

export const KvalitetPatternsTable = ({ patterns, onShowWeek }: KvalitetPatternsTableProps) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
      Mönster per vecka
    </div>
    {patterns.length === 0 ? (
      <p className="py-6 text-center text-sm text-slate-500">
        Ingen rotorsak har nått tre dåliga utfall under en vecka i perioden.
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-2 py-2">Rotorsak</th>
              <th className="px-2 py-2">Vecka</th>
              <th className="px-2 py-2">Antal nu</th>
              <th className="px-2 py-2">Larmat</th>
              <th className="px-2 py-2">Exempel</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {patterns.map((pattern) => (
              <tr key={`${pattern.isoYear}-${pattern.isoWeek}-${pattern.rootCause}`}>
                <td className="px-2 py-2 font-medium text-slate-900">
                  {rootCauseLabel(pattern.rootCause)}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                  v{pattern.isoWeek} {pattern.isoYear}
                </td>
                <td className="px-2 py-2 text-slate-900">{pattern.currentCount}</td>
                <td className="px-2 py-2 whitespace-nowrap text-slate-600">
                  {pattern.alertedCount === null
                    ? 'Inte larmat'
                    : `vid ${pattern.alertedCount}, ${formatDateTime(pattern.alertedAt)}`}
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-2">
                    {pattern.exampleSessionIds.map((id) => (
                      <KvalitetSessionLink key={id} sessionId={id} />
                    ))}
                  </div>
                </td>
                <td className="px-2 py-2">
                  <div className="flex items-start justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onShowWeek(pattern)}
                      className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1 text-xs text-slate-700 transition hover:bg-slate-50"
                    >
                      Visa sessioner
                    </button>
                    <PatternTicketButton pattern={pattern} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);
